import { captureSupportScope, internalApiHeaders } from '../admin/supportTransport';
import { currentAssessmentQuestion } from '../utils/currentAssessment';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Custom error classes
export class SubscriptionRequiredError extends Error {
  constructor(message: string = 'Pro subscription required for AI features') {
    super(message);
    this.name = 'SubscriptionRequiredError';
  }
}

export interface Company {
  id: string;
  name: string;
  size: string;
  industry: string;
  description: string;
  logo?: string;
  clerkOrgId?: string;
  subscriptionTier: string;
  createdAt: string;
  role?: string;
  isDemo?: boolean;
  adviserPracticeId?: string | null;
  accessSource?: 'adviser' | 'direct';
  capabilities?: {
    editPlan: boolean;
    editOrganisation: boolean;
    manageMembers: boolean;
    manageBilling: boolean;
    deleteCompany: boolean;
    planningAccess: boolean;
  };
  joinedAt?: string;
}

export interface CreateCompanyData {
  name: string;
  size: string;
  industry: string;
  description: string;
}

export interface InviteUserData {
  email: string;
  role: string;
}

export interface InviteResponse {
  email: string;
  role: string;
  status: 'added' | 'invitation_sent';
  invitationId?: string;
  expiresAt?: number;
}

export interface PendingInvitation {
  id: string;
  companyId: string;
  companyName: string;
  role: string;
  invitedEmail: string;
  createdAt: string;
  expiresAt?: string;
  clerkInvitationId: string;
  clerkOrgId: string;
  inviterName?: string;
}

export interface AcceptInvitationResponse {
  companyId: string;
  role: string;
}

export interface CurrentUser {
  id: string;
  clerkId: string;
  email: string;
  name: string;
  title?: string;
  photo?: string;
  superUser: boolean;
  isFounder: boolean;
  hasAdviserAccess?: boolean;
  founderSubscriptionStatus?: string | null;
  founderSubscriptionEnd?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUser {
  id: string;
  clerkId: string;
  email: string;
  name: string;
  title?: string;
  photo?: string;
  superUser: boolean;
  maxCompanyCreations: number;
  createdAt: string;
  updatedAt: string;
  companyMemberships: number;
  clerkData?: {
    lastSignInAt: Date | null;
    createdAt: Date;
    emailVerified: boolean;
    twoFactorEnabled: boolean;
    banned: boolean;
    locked: boolean;
  } | null;
}

export interface AdminCompany {
  id: string;
  isDemo?: boolean;
  clerkOrgId: string;
  name: string;
  size: string;
  industry: string;
  description: string;
  logo?: string;
  subscriptionTier: string;
  billingStatus: string;
  isPro: boolean;
  proUserCount: number;
  activeUserCount: number;
  maxUsers: number;
  maxStrategies: number;
  isInTrial: boolean;
  trialStartDate?: string;
  trialEndDate?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  owner?: {
    id: string;
    name: string;
    email: string;
    photo?: string;
  } | null;
  recentActivityCount: number;
}

export interface AdminStats {
  overview: {
    totalUsers: number;
    totalCompanies: number;
    superUsers: number;
    activeCompanies: number;
  };
  growth: {
    newUsersLast30Days: number;
    newCompaniesLast30Days: number;
  };
  subscriptions: Record<string, number>;
}

export interface TeamMember {
  id: string;
  membershipId?: string; // Membership ID for admin operations (role updates, removal)
  adviserPracticeId?: string | null;
  name: string;
  email: string;
  photo?: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: string;
  status: 'active' | 'invited';
  inviterName?: string | null;
}

export interface UpdateCompanyData {
  name?: string;
  size?: string;
  industry?: string;
  description?: string;
  logo?: string | null;
}

export interface UpdateRoleData {
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

export interface CompanyInvitation {
  id: string;
  clerkInvitationId: string;
  clerkOrgId: string;
  invitedEmail: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  createdAt: string;
  updatedAt: string;
  acceptedAt?: string | null;
  expiresAt?: string | null;
  inviterName?: string | null;
}

// New organizational structure types
export interface Division {
  id: string;
  companyId: string;
  parentDivisionId?: string;
  name: string;
  description?: string;
  type: 'business_unit' | 'functional' | 'geographical' | 'product' | 'project' | 'custom';
  code?: string;
  color?: string;
  managerId?: string;
  budget?: string;
  headcount: number;
  isActive: boolean;
  sortOrder: number;
  metadata?: {
    kpis?: Array<{ name: string; value: number; unit: string }>;
    objectives?: string[];
    customFields?: { [key: string]: any };
  };
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // Manager info (populated in queries)
  managerName?: string;
  managerEmail?: string;
  managerPhoto?: string;
  // Hierarchy data
  children?: Division[];
  teams?: Team[];
  strategies?: Strategy[];
}

export interface Team {
  id: string;
  companyId: string;
  divisionId?: string;
  parentTeamId?: string;
  name: string;
  description?: string;
  type: 'operational' | 'project' | 'cross_functional' | 'leadership' | 'custom';
  code?: string;
  leadId?: string;
  memberCount: number;
  isActive: boolean;
  sortOrder: number;
  settings?: {
    isPublic?: boolean;
    allowSelfJoin?: boolean;
    maxMembers?: number;
    skills?: string[];
  };
  metadata?: {
    kpis?: Array<{ name: string; value: number; unit: string }>;
    objectives?: string[];
    customFields?: { [key: string]: any };
  };
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // Lead info (populated in queries)
  leadName?: string;
  leadEmail?: string;
  leadPhoto?: string;
  // Hierarchy data
  children?: Team[];
  members?: OrganizationalTeamMember[];
  division?: Division;
}

export interface OrganizationalTeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: string; // Team-specific role (lead, member, contributor)
  joinedAt: string;
  invitedBy?: string;
  isActive: boolean;
  // User info (populated in queries)
  userName?: string;
  userEmail?: string;
  userPhoto?: string;
}

export interface Strategy {
  id: string;
  companyId: string;
  name: string;
  summary?: string;
  status: 'draft' | 'active' | 'archived';
  startDate?: string;
  endDate?: string;
  ownerType: 'company' | 'division' | 'team';
  ownerId?: string;
  parentStrategyId?: string;
  level: number;
  sortOrder: number;
  isTemplate: boolean;
  alignmentScore?: number;
  tags?: string[];
  stakeholders?: Array<{
    userId: string;
    role: string;
    permissions: string[];
  }>;
  vision?: string;
  mission?: string;
  values?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // Hierarchy data
  parent?: Strategy;
  children?: Strategy[];
  owner?: {
    type: 'company' | 'division' | 'team';
    id: string;
    name: string;
  };
}

export interface OrganizationalNode {
  id: string;
  type: 'company' | 'division' | 'team';
  name: string;
  parentId?: string;
  parentType?: 'company' | 'division' | 'team';
  level: number;
  children: OrganizationalNode[];
  strategies: Strategy[];
  objectives: any[];
  tasks: any[];
  metadata?: any;
}

export interface CreateDivisionData {
  name: string;
  description?: string;
  type?: 'business_unit' | 'functional' | 'geographical' | 'product' | 'project' | 'custom';
  code?: string;
  color?: string;
  managerId?: string;
  parentDivisionId?: string;
  budget?: number;
  headcount?: number;
  sortOrder?: number;
  metadata?: any;
}

export interface CreateTeamData {
  name: string;
  description?: string;
  type?: 'operational' | 'project' | 'cross_functional' | 'leadership' | 'custom';
  code?: string;
  leadId?: string;
  divisionId?: string;
  parentTeamId?: string;
  sortOrder?: number;
  settings?: {
    isPublic?: boolean;
    allowSelfJoin?: boolean;
    maxMembers?: number;
    skills?: string[];
  };
  metadata?: any;
}

export interface CreateStrategyData {
  name: string;
  summary?: string;
  ownerType: 'company' | 'division' | 'team';
  ownerId: string;
  parentStrategyId?: string;
  vision?: string;
  mission?: string;
  values?: string[];
  tags?: string[];
  startDate?: string;
  endDate?: string;
}

export interface UpdateInvitationStatusData {
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

export interface ObjectiveType {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  isDefault: boolean;
  companyId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateObjectiveTypeData {
  name: string;
  description?: string;
  color: string;
  icon: string;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'not_started' | 'in_progress' | 'completed' | 'blocked';
  percentComplete: number;
  progressMode?: 'calculated' | 'manual';
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
  estimatedHours?: number;
  actualHours?: number;
  parentTaskId?: string;
  sortOrder: number;
  objectiveId?: string;
  objectiveName?: string;
  objectiveCategory?: string;
  strategyId?: string;
  strategyName?: string;
  projectId?: string;
  projectName?: string;
  companyId?: string;
  companyName?: string;
  assignedTo?: string;
  assigneeName?: string;
  assigneeEmail?: string;
  assigneePhoto?: string;
  // NEW: Position-based assignment information
  assignedPositionId?: string;
  positionTitle?: string;
  positionDepartment?: string;
  positionUsers?: Array<{
    id: string;
    name: string;
    email: string;
    photo?: string;
    isPrimary: boolean;
  }>;
  // NEW: Reference numbering
  referenceNumber?: string;
  displayNumber?: string;
  // NEW: AI generation tracking
  aiGenerated?: boolean;
  isCreatedByMe: boolean;
  isAssignedToMe: boolean;
}

export interface Project {
  id: string;
  strategyId?: string;
  companyId: string;
  title: string;
  description?: string;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'high' | 'medium' | 'low';
  startDate?: string;
  targetDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  projectManager?: string;
  projectManagerName?: string;
  assignedTo?: string;
  assigneeName?: string;
  // NEW: Position-based assignment fields
  projectManagerPositionId?: string;
  assignedPositionId?: string;
  divisionId?: string;
  teamId?: string;
  progress: number;
  progressMode?: 'calculated' | 'manual';
  estimatedHours?: number;
  actualHours?: number;
  budgetAllocated?: number;
  budgetUsed?: number;
  tags?: string[];
  stakeholders?: string[];
  deliverables?: Array<{
    name: string;
    description?: string;
    dueDate?: string;
    status: 'pending' | 'in_progress' | 'completed';
    assignedTo?: string;
  }>;
  risks?: Array<{
    description: string;
    impact: 'low' | 'medium' | 'high';
    probability: 'low' | 'medium' | 'high';
    mitigation?: string;
    status: 'open' | 'mitigated' | 'closed';
  }>;
  dependencies?: {
    blockedBy?: string[];
    blocks?: string[];
    relatedProjects?: string[];
  };
  notes?: string;
  metadata?: {
    methodology?: 'agile' | 'waterfall' | 'hybrid';
    complexity?: 'low' | 'medium' | 'high';
    customerImpact?: 'low' | 'medium' | 'high';
    resources?: Array<{ type: string; quantity: number; unit: string }>;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // NEW: Reference numbering
  referenceNumber?: string;
  displayNumber?: string;
  // NEW: AI generation tracking
  aiGenerated?: boolean;
  // Additional calculated fields
  taskCount?: number;
  completedTasks?: number;
  strategyTitle?: string;
  strategyObjectiveId?: string;
}

export interface TasksResponse {
  myTasks: Task[];
  tasksIAssigned: Task[];
  all: Task[];
}

export interface TaskStats {
  myTasks: {
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
    blocked: number;
  };
  tasksIAssigned: {
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
    blocked: number;
  };
  priority: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

export interface UpdateTaskStatusData {
  status?: 'not_started' | 'in_progress' | 'completed' | 'blocked';
  percentComplete?: number;
}

export interface OrganizationalPosition {
  id: string;
  title: string;
  description?: string;
  department?: string;
  level: number;
  parentPositionId?: string;
  divisionId?: string;
  teamId?: string;
  isActive: boolean;
  createdAt: string;
  assignments: PositionAssignment[];
  // Additional populated fields
  divisionName?: string;
  teamName?: string;
}

export interface PositionAssignment {
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  assignedAt: string;
  isPrimary: boolean;
}

export interface CreatePositionData {
  title: string;
  description?: string;
  department?: string;
  level?: number;
  parentPositionId?: string;
  divisionId?: string;
  teamId?: string;
}

export interface UpdatePositionData {
  title?: string;
  description?: string;
  department?: string;
  level?: number;
  parentPositionId?: string;
  divisionId?: string;
  teamId?: string;
}

export interface AssignUserToPositionData {
  assignUserId: string;
  isPrimary?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
  currentRevision?: number;
  code?: string;
}

// Helper function to get auth token from Clerk
async function getAuthToken(): Promise<string | null> {
  try {
    // Access Clerk instance from window
    const clerk = (window as any).Clerk;
    if (!clerk?.user) return null;

    // Get session token
    const session = clerk.session;
    if (!session) return null;

    return await session.getToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

// Helper function to make API requests
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const requestScope = captureSupportScope();
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: internalApiHeaders(
        `${API_BASE_URL}${endpoint}`,
        {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        false,
        requestScope
      ),
    });

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Not JSON response - likely an HTML error page
      const text = await response.text();
      console.error('Non-JSON response received:', {
        status: response.status,
        statusText: response.statusText,
        url: `${API_BASE_URL}${endpoint}`,
        contentType,
        responseText: text.substring(0, 200) + '...',
      });

      return {
        success: false,
        error: `Server error: ${response.status} ${response.statusText}`,
        status: response.status,
      };
    }

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.error || 'API request failed';

      // Check if this is a subscription error
      if (
        errorMessage.includes('Pro subscription required') ||
        errorMessage.includes('SubscriptionRequiredError')
      ) {
        throw new SubscriptionRequiredError(errorMessage);
      }

      return {
        success: false,
        error: errorMessage,
        status: response.status,
        ...(typeof data.currentRevision === 'number'
          ? { currentRevision: data.currentRevision }
          : {}),
        ...(typeof data.code === 'string' ? { code: data.code } : {}),
      };
    }

    return data;
  } catch (error) {
    console.error('API request error:', error);

    // Re-throw SubscriptionRequiredError as-is for specific handling
    if (error instanceof SubscriptionRequiredError) {
      throw error;
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Company API functions
export const companiesApi = {
  // Create a new company
  async create(companyData: CreateCompanyData): Promise<ApiResponse<Company>> {
    return apiRequest<Company>('/companies', {
      method: 'POST',
      body: JSON.stringify(companyData),
    });
  },

  // Get all companies for current user
  async list(): Promise<ApiResponse<Company[]>> {
    return apiRequest<Company[]>('/companies');
  },

  // Get a specific company by ID
  async get(id: string): Promise<ApiResponse<Company>> {
    return apiRequest<Company>(`/companies/${id}`);
  },

  // Invite user to company
  async inviteUser(
    companyId: string,
    inviteData: InviteUserData
  ): Promise<ApiResponse<InviteResponse>> {
    return apiRequest<InviteResponse>(`/companies/${companyId}/invite`, {
      method: 'POST',
      body: JSON.stringify(inviteData),
    });
  },

  // Get pending invitations for current user
  async getPendingInvitations(): Promise<ApiResponse<PendingInvitation[]>> {
    return apiRequest<PendingInvitation[]>('/companies/invitations/pending');
  },

  // Accept a pending invitation
  async acceptInvitation(invitationId: string): Promise<ApiResponse<AcceptInvitationResponse>> {
    return apiRequest<AcceptInvitationResponse>(`/companies/invitations/${invitationId}/accept`, {
      method: 'POST',
    });
  },

  // Get team members for a company (including invited users)
  async getTeamMembers(companyId: string): Promise<ApiResponse<TeamMember[]>> {
    return apiRequest<TeamMember[]>(`/companies/${companyId}/members`);
  },

  // Update company details (admin only)
  async updateCompany(
    companyId: string,
    updateData: UpdateCompanyData
  ): Promise<ApiResponse<Company>> {
    return apiRequest<Company>(`/companies/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  },

  // Update member role (admin only)
  async updateMemberRole(
    companyId: string,
    memberId: string,
    roleData: UpdateRoleData
  ): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/companies/${companyId}/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
  },

  // Remove member from company (admin only)
  async removeMember(companyId: string, memberId: string): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/companies/${companyId}/members/${memberId}`, {
      method: 'DELETE',
    });
  },

  // Get all invitations for a company (admin only)
  async getCompanyInvitations(companyId: string): Promise<ApiResponse<CompanyInvitation[]>> {
    return apiRequest<CompanyInvitation[]>(`/companies/${companyId}/invitations`);
  },

  // Update invitation status (admin only)
  async updateInvitationStatus(
    companyId: string,
    invitationId: string,
    statusData: UpdateInvitationStatusData
  ): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/companies/${companyId}/invitations/${invitationId}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  },

  // Sync and cleanup hanging invitations (admin only)
  async syncInvitations(companyId: string): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/companies/${companyId}/invitations/sync`, {
      method: 'POST',
    });
  },

  // Delete company (owner only)
  async deleteCompany(companyId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/companies/${companyId}`, {
      method: 'DELETE',
    });
  },

  // Foundation data management
  saveFoundation: async (companyId: string, foundationData: any): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/companies/${companyId}/foundation`, {
      method: 'POST',
      body: JSON.stringify({ foundationData }),
    });
  },

  // PATCH Foundation data (partial updates for autosave)
  updateFoundationFields: async (
    companyId: string,
    changedFields: any
  ): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/companies/${companyId}/foundation`, {
      method: 'PATCH',
      body: JSON.stringify({ changedFields }),
    });
  },

  loadFoundation: async (companyId: string): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/companies/${companyId}/foundation`);
  },

  // Update foundation progress
  updateFoundationProgress: async (
    companyId: string,
    progress: number
  ): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/companies/${companyId}/foundation/progress`, {
      method: 'PUT',
      body: JSON.stringify({ progress }),
    });
  },

  // Update external assessment progress
  updateExternalAssessmentProgress: async (
    companyId: string,
    progress: number
  ): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/companies/${companyId}/external-assessment/progress`, {
      method: 'PUT',
      body: JSON.stringify({ progress }),
    });
  },

  // Update internal assessment progress
  updateInternalAssessmentProgress: async (
    companyId: string,
    progress: number
  ): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/companies/${companyId}/internal-assessment/progress`, {
      method: 'PUT',
      body: JSON.stringify({ progress }),
    });
  },

  // Calculate and update foundation progress based on current data
  calculateAndUpdateFoundationProgress: async (
    companyId: string
  ): Promise<ApiResponse<{ progress: number }>> => {
    try {
      // Load the current foundation data
      const foundationResponse = await companiesApi.loadFoundation(companyId);
      if (!foundationResponse.success || !foundationResponse.data?.foundationData) {
        return { success: false, error: 'Failed to load foundation data' };
      }

      const foundationData = foundationResponse.data.foundationData;

      // Calculate foundation progress
      let totalElements = 0;
      let completedElements = 0;

      // Check Vision (3 tabs)
      if (foundationData.vision) {
        totalElements += 3;
        ['vision1', 'vision2', 'vision3'].forEach((tab) => {
          if (foundationData.vision[tab] && foundationData.vision[tab].trim().length > 20) {
            completedElements++;
          }
        });
      } else {
        totalElements += 3;
      }

      // Check Mission (3 tabs)
      if (foundationData.mission) {
        totalElements += 3;
        ['mission1', 'mission2', 'mission3'].forEach((tab) => {
          if (foundationData.mission[tab] && foundationData.mission[tab].trim().length > 20) {
            completedElements++;
          }
        });
      } else {
        totalElements += 3;
      }

      // Check Values (count total values across all tabs, complete if 5+ total)
      totalElements += 1; // Treat values as a single element
      if (foundationData.values) {
        let totalValues = 0;
        ['values1', 'values2', 'values3'].forEach((tab) => {
          const v = foundationData.values[tab];
          if (Array.isArray(v)) {
            totalValues += v.length;
          } else if (typeof v === 'string' && v.trim()) {
            try {
              const parsed = JSON.parse(v);
              if (Array.isArray(parsed)) {
                totalValues += parsed.length;
              }
            } catch {
              // If it's not valid JSON, treat as comma-separated string
              const values = v
                .split(',')
                .map((val) => val.trim())
                .filter((val) => val.length > 0);
              totalValues += values.length;
            }
          }
        });
        if (totalValues >= 5) {
          completedElements++;
        }
      }

      // Check Objectives (4 categories)
      if (foundationData.objectives) {
        totalElements += 4;
        ['financial', 'customer', 'process', 'talent'].forEach((category) => {
          if (
            foundationData.objectives[category] &&
            Array.isArray(foundationData.objectives[category])
          ) {
            const validObjectives = foundationData.objectives[category].filter(
              (obj: any) =>
                obj.goal &&
                obj.goal.trim().length > 10 &&
                obj.measure &&
                obj.measure.trim().length > 5 &&
                obj.byWhen &&
                obj.byWhen.trim().length > 5
            );
            if (validObjectives.length >= 2) {
              completedElements++;
            }
          }
        });
      } else {
        totalElements += 4;
      }

      const calculatedProgress =
        totalElements > 0 ? Math.round((completedElements / totalElements) * 100) : 0;

      // Update the backend with the calculated progress
      const updateResponse = await companiesApi.updateFoundationProgress(
        companyId,
        calculatedProgress
      );

      if (updateResponse.success) {
        return { success: true, data: { progress: calculatedProgress } };
      } else {
        return { success: false, error: updateResponse.error || 'Failed to update progress' };
      }
    } catch (error) {
      return { success: false, error: 'Failed to calculate foundation progress' };
    }
  },

  // Get strategy progress
  getStrategyProgress: async (
    companyId: string
  ): Promise<
    ApiResponse<{
      foundationProgress: number;
      externalAssessmentProgress: number;
      internalAssessmentProgress: number;
      formulationProgress: number;
      executionProgress: number;
    }>
  > => {
    return apiRequest<{
      foundationProgress: number;
      externalAssessmentProgress: number;
      internalAssessmentProgress: number;
      formulationProgress: number;
      executionProgress: number;
    }>(`/companies/${companyId}/strategy/progress`);
  },

  // Generate Foundation content with AI
  generateFoundation: async (
    companyId: string,
    type: 'vision' | 'mission' | 'values' | 'objectives',
    options?: {
      currentContent?: string;
      additionalContext?: string;
    }
  ): Promise<
    ApiResponse<{
      type: string;
      content: any;
      usage: any;
      model: string;
    }>
  > => {
    const response = await apiRequest<{
      type: string;
      content: any;
      usage: any;
      model: string;
    }>(`/companies/${companyId}/generate-foundation/${type}`, {
      method: 'POST',
      body: JSON.stringify({
        currentContent: options?.currentContent || '',
        additionalContext: options?.additionalContext || '',
      }),
    });

    return response;
  },

  // Get aggregated summary analysis data
  getSummaryAnalysis: async (
    companyId: string
  ): Promise<
    ApiResponse<{
      external: {
        pestel: {
          [key: string]: { positive: string[]; negative: string[] };
        };
        fiveForces: {
          [key: string]: { level: string; explanation: string };
        };
        drivingForces: Array<{
          name: string;
          direction: string;
          commentary: string;
        }>;
        competitors: Array<{
          name: string;
          marketScope: string;
          strengths: string;
          weaknesses: string;
        }>;
      };
      internal: {
        vision: {
          statement: string;
          score: number;
          alternatives: string[];
        };
        mission: {
          statement: string;
          score: number;
        };
        values: Array<{
          name: string;
          status: string;
        }>;
        objectives: {
          financial: Array<{ objective: string; measure: string; target: string }>;
          customer: Array<{ objective: string; measure: string; target: string }>;
          process: Array<{ objective: string; measure: string; target: string }>;
          talent: Array<{ objective: string; measure: string; target: string }>;
        };
        swot: {
          strengths: string[];
          weaknesses: string[];
          opportunities: string[];
          threats: string[];
        };
        successFactors: Array<{
          factor: string;
          status: string;
        }>;
      };
    }>
  > => {
    return apiRequest<any>(`/companies/${companyId}/summary-analysis`);
  },

  // Mark plan summary as visited
  markSummaryVisited: async (companyId: string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest<{ message: string }>(`/companies/${companyId}/formulation/summary-visited`, {
      method: 'POST',
    });
  },

  // Generate strategy recommendations with AI
  generateStrategyRecommendations: async (
    companyId: string,
    options: {
      objective: {
        goal: string;
        measure?: string;
        category: string;
        priority: string;
      };
      objectiveType: {
        name: string;
        description?: string;
      };
      availableStrategies: Array<{
        id: string;
        title: string;
        description: string;
        category: string;
      }>;
      additionalContext?: string;
    }
  ): Promise<
    ApiResponse<{
      recommendedStrategyIds: string[];
      reasoning: string;
      totalStrategiesAnalyzed: number;
      recommendedCount: number;
      usage: any;
      model: string;
    }>
  > => {
    const response = await apiRequest<{
      recommendedStrategyIds: string[];
      reasoning: string;
      totalStrategiesAnalyzed: number;
      recommendedCount: number;
      usage: any;
      model: string;
    }>(`/companies/${companyId}/generate-strategy-recommendations`, {
      method: 'POST',
      body: JSON.stringify(options),
    });

    return response;
  },

  // Generate custom strategies with AI
  generateCustomStrategies: async (
    companyId: string,
    options: {
      objective: {
        goal: string;
        measure?: string;
        category: string;
        priority: string;
      };
      objectiveType: {
        name: string;
        description?: string;
      };
      additionalContext?: string;
      strategyCount?: number;
    }
  ): Promise<
    ApiResponse<{
      strategies: Array<{
        title: string;
        description: string;
        category: string;
        priority: 'high' | 'medium' | 'low';
        rationale: string;
        implementation_approach: string;
        success_metrics: string[];
        timeframe: string;
      }>;
      reasoning: string;
      strategiesGenerated: number;
      usage: any;
      model: string;
    }>
  > => {
    const response = await apiRequest<{
      strategies: Array<{
        title: string;
        description: string;
        category: string;
        priority: 'high' | 'medium' | 'low';
        rationale: string;
        implementation_approach: string;
        success_metrics: string[];
        timeframe: string;
      }>;
      reasoning: string;
      strategiesGenerated: number;
      usage: any;
      model: string;
    }>(`/companies/${companyId}/generate-custom-strategies`, {
      method: 'POST',
      body: JSON.stringify(options),
    });

    return response;
  },

  // Generate reference numbers for all execution items
  generateReferenceNumbers: async (
    companyId: string
  ): Promise<
    ApiResponse<{
      objectivesAssigned: number;
      strategiesAssigned: number;
      projectsAssigned: number;
      tasksAssigned: number;
      nextAvailableNumber: number;
    }>
  > => {
    const response = await apiRequest<{
      objectivesAssigned: number;
      strategiesAssigned: number;
      projectsAssigned: number;
      tasksAssigned: number;
      nextAvailableNumber: number;
    }>(`/companies/${companyId}/reference-numbers`, {
      method: 'PUT',
    });

    return response;
  },
};

// Competitors API
export const competitorsApi = {
  // Get all competitors for a company
  getCompetitors: async (companyId: string): Promise<ApiResponse<any[]>> => {
    return apiRequest<any[]>(`/competitors/${companyId}`);
  },

  // Create a new competitor
  createCompetitor: async (companyId: string, competitorData: any): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/competitors/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(competitorData),
    });
  },

  // Update a competitor
  updateCompetitor: async (
    competitorId: string,
    competitorData: any
  ): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/competitors/${competitorId}`, {
      method: 'PUT',
      body: JSON.stringify(competitorData),
    });
  },

  // Delete a competitor
  deleteCompetitor: async (competitorId: string): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/competitors/${competitorId}`, {
      method: 'DELETE',
    });
  },

  // Trigger AI scraping for a competitor
  scrapeCompetitor: async (competitorId: string): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/competitors/${competitorId}/scrape`, {
      method: 'POST',
    });
  },
};

// Tasks API functions
export const tasksApi = {
  // Get all tasks for current user across all companies
  async list(filters?: {
    status?: string;
    priority?: string;
    company?: string;
  }): Promise<ApiResponse<TasksResponse>> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.company) params.append('company', filters.company);

    const queryString = params.toString();
    return apiRequest<TasksResponse>(`/tasks${queryString ? `?${queryString}` : ''}`);
  },

  // Create a new task
  async create(taskData: {
    title: string;
    description?: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    status: 'not_started' | 'in_progress' | 'completed' | 'blocked';
    dueDate?: string;
    assignedTo?: string;
    assignedPositionId?: string;
    objectiveId?: string;
    projectId?: string;
    companyId?: string;
    parentTaskId?: string;
    estimatedHours?: number;
    percentComplete?: number;
    dependencies?: string[];
  }): Promise<ApiResponse<Task>> {
    return apiRequest<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
  },

  // Update a task
  async update(
    taskId: string,
    taskData: {
      title?: string;
      description?: string;
      priority?: 'low' | 'medium' | 'high' | 'critical';
      status?: 'not_started' | 'in_progress' | 'completed' | 'blocked';
      dueDate?: string;
      assignedTo?: string;
      assignedPositionId?: string;
      estimatedHours?: number;
      actualHours?: number;
      percentComplete?: number;
      progressMode?: 'calculated' | 'manual';
      objectiveId?: string;
      companyId?: string;
      parentTaskId?: string;
      referenceNumber?: string;
      displayNumber?: string;
    }
  ): Promise<ApiResponse<Task>> {
    return apiRequest<Task>(`/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(taskData),
    });
  },

  // Delete a task
  async delete(taskId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/tasks/${taskId}`, {
      method: 'DELETE',
    });
  },

  // Orphan a task from its project
  async orphan(taskId: string): Promise<ApiResponse<{ message: string; data: Task }>> {
    return apiRequest<{ message: string; data: Task }>(`/tasks/${taskId}/orphan`, {
      method: 'PUT',
    });
  },

  // Generate tasks with AI for a project
  async generateWithAI(
    companyId: string,
    projectId: string,
    additionalContext?: string,
    documentContent?: string,
    documentName?: string
  ): Promise<
    ApiResponse<{
      tasks: Task[];
      allTasks?: Task[];
      aiMetadata: {
        usage: any;
        model: string;
        tasksGenerated: number;
        totalTasksCreated?: number;
        subtasksCreated?: number;
      };
    }>
  > {
    const response = await apiRequest<{
      tasks: Task[];
      allTasks?: Task[];
      aiMetadata: {
        usage: any;
        model: string;
        tasksGenerated: number;
        totalTasksCreated?: number;
        subtasksCreated?: number;
      };
    }>(`/tasks/${companyId}/generate-ai`, {
      method: 'POST',
      body: JSON.stringify({
        projectId,
        additionalContext,
        documentContent,
        documentName,
      }),
    });

    return response;
  },

  // Generate sub-tasks with AI for a parent task
  async generateWithAISubTasks(
    companyId: string,
    parentTaskId: string,
    additionalContext?: string
  ): Promise<
    ApiResponse<{
      subTasks: Task[];
      parentTask: {
        id: string;
        title: string;
      };
      aiMetadata: {
        usage: any;
        model: string;
        subTasksGenerated: number;
      };
    }>
  > {
    const response = await apiRequest<{
      subTasks: Task[];
      parentTask: {
        id: string;
        title: string;
      };
      aiMetadata: {
        usage: any;
        model: string;
        subTasksGenerated: number;
      };
    }>(`/tasks/${companyId}/generate-subtasks-ai`, {
      method: 'POST',
      body: JSON.stringify({
        parentTaskId,
        additionalContext,
      }),
    });

    return response;
  },

  // Update task status
  async updateStatus(taskId: string, statusData: UpdateTaskStatusData): Promise<ApiResponse<Task>> {
    return apiRequest<Task>(`/tasks/${taskId}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  },

  // Get task statistics
  async getStats(): Promise<ApiResponse<TaskStats>> {
    return apiRequest<TaskStats>('/tasks/stats');
  },

  // Get objectives for task creation
  async getObjectives(): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        strategyName: string;
        companyId: string;
        companyName: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        name: string;
        strategyName: string;
        companyId: string;
        companyName: string;
      }>
    >('/tasks/objectives');
  },

  // Reorder tasks for drag-and-drop
  async reorder(
    taskId: string,
    newParentId?: string,
    newSortOrder?: number
  ): Promise<ApiResponse<Task>> {
    return apiRequest<Task>('/tasks/reorder', {
      method: 'PUT',
      body: JSON.stringify({
        taskId,
        newParentId,
        newSortOrder,
      }),
    });
  },

  // Get users for task assignment
  async getUsers(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        email: string;
        photo?: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        name: string;
        email: string;
        photo?: string;
      }>
    >(`/tasks/users/${companyId}`);
  },
};

// Projects API functions
export const projectsApi = {
  // Get projects for a specific strategy
  async getProjectsForStrategy(
    companyId: string,
    strategyId: string
  ): Promise<ApiResponse<Project[]>> {
    return apiRequest<Project[]>(`/projects/${companyId}/strategy/${strategyId}`);
  },

  // Get all projects for a company (including orphaned projects)
  async getAllProjects(companyId: string): Promise<ApiResponse<Project[]>> {
    return apiRequest<Project[]>(`/projects/${companyId}`);
  },

  // Create a new project
  async create(
    companyId: string,
    projectData: {
      strategyId?: string;
      title: string;
      description?: string;
      status?: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
      priority?: 'high' | 'medium' | 'low';
      startDate?: string;
      targetDate?: string;
      projectManager?: string;
      assignedTo?: string;
      divisionId?: string;
      teamId?: string;
      estimatedHours?: number;
      budgetAllocated?: number;
      tags?: string[];
      deliverables?: Array<{
        name: string;
        description?: string;
        dueDate?: string;
        status: 'pending' | 'in_progress' | 'completed';
        assignedTo?: string;
      }>;
      notes?: string;
    }
  ): Promise<ApiResponse<Project>> {
    return apiRequest<Project>(`/projects/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  },

  // Update a project
  async update(
    projectId: string,
    updateData: Partial<{
      title: string;
      description?: string;
      status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
      priority: 'high' | 'medium' | 'low';
      startDate?: string;
      targetDate?: string;
      actualStartDate?: string;
      actualEndDate?: string;
      projectManager?: string;
      assignedTo?: string;
      projectManagerPositionId?: string;
      assignedPositionId?: string;
      divisionId?: string;
      teamId?: string;
      progress: number;
      progressMode?: 'calculated' | 'manual';
      estimatedHours?: number;
      actualHours?: number;
      budgetAllocated?: number;
      budgetUsed?: number;
      tags?: string[];
      deliverables?: Array<{
        name: string;
        description?: string;
        dueDate?: string;
        status: 'pending' | 'in_progress' | 'completed';
        assignedTo?: string;
      }>;
      notes?: string;
      referenceNumber?: string;
      displayNumber?: string;
    }>
  ): Promise<ApiResponse<Project>> {
    return apiRequest<Project>(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  },

  // Delete a project with cascade/orphan options
  async delete(
    projectId: string,
    action?: 'cascade' | 'orphan'
  ): Promise<
    ApiResponse<{
      message: string;
      deletedTasks?: number;
      orphanedTasks?: number;
    }>
  > {
    const url = action ? `/projects/${projectId}?action=${action}` : `/projects/${projectId}`;
    return apiRequest<{
      message: string;
      deletedTasks?: number;
      orphanedTasks?: number;
    }>(url, {
      method: 'DELETE',
    });
  },

  // Orphan a project from its strategy
  async orphan(projectId: string): Promise<ApiResponse<{ message: string; data: Project }>> {
    return apiRequest<{ message: string; data: Project }>(`/projects/${projectId}/orphan`, {
      method: 'PUT',
    });
  },

  // Generate projects with AI for a strategy
  async generateWithAI(
    companyId: string,
    strategyId: string,
    additionalContext?: string
  ): Promise<
    ApiResponse<{
      projects: Project[];
      aiMetadata: {
        usage: any;
        model: string;
      };
    }>
  > {
    const response = await apiRequest<{
      projects: Project[];
      aiMetadata: {
        usage: any;
        model: string;
      };
    }>(`/projects/${companyId}/generate-ai`, {
      method: 'POST',
      body: JSON.stringify({
        strategyId,
        additionalContext,
      }),
    });

    return response;
  },

  // Get users for project assignment
  async getUsers(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        email: string;
        photo: string | null;
        role: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        name: string;
        email: string;
        photo: string | null;
        role: string;
      }>
    >(`/projects/${companyId}/users`);
  },
};

// Positions API for assignment dropdowns
export const positionsApi = {
  // Get all positions with assigned users for assignment dropdowns
  async getPositions(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        title: string;
        description?: string;
        department?: string;
        level: number;
        divisionId?: string;
        teamId?: string;
        assignedUsers: Array<{
          id: string;
          name: string;
          email: string;
          photo?: string;
          isPrimary: boolean;
          assignedAt: string;
        }>;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        title: string;
        description?: string;
        department?: string;
        level: number;
        divisionId?: string;
        teamId?: string;
        assignedUsers: Array<{
          id: string;
          name: string;
          email: string;
          photo?: string;
          isPrimary: boolean;
          assignedAt: string;
        }>;
      }>
    >(`/positions/${companyId}`);
  },
};

// Org Chart API functions
export const orgChartApi = {
  // Get all positions for a company
  async getPositions(companyId: string): Promise<ApiResponse<OrganizationalPosition[]>> {
    return apiRequest<OrganizationalPosition[]>(`/org-chart/${companyId}/positions`);
  },

  // Create a new position
  async createPosition(
    companyId: string,
    positionData: CreatePositionData
  ): Promise<ApiResponse<OrganizationalPosition>> {
    return apiRequest<OrganizationalPosition>(`/org-chart/${companyId}/positions`, {
      method: 'POST',
      body: JSON.stringify(positionData),
    });
  },

  // Update a position
  async updatePosition(
    companyId: string,
    positionId: string,
    positionData: UpdatePositionData
  ): Promise<ApiResponse<OrganizationalPosition>> {
    return apiRequest<OrganizationalPosition>(`/org-chart/${companyId}/positions/${positionId}`, {
      method: 'PUT',
      body: JSON.stringify(positionData),
    });
  },

  // Delete a position
  async deletePosition(companyId: string, positionId: string): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/org-chart/${companyId}/positions/${positionId}`, {
      method: 'DELETE',
    });
  },

  // Assign user to position
  async assignUserToPosition(
    companyId: string,
    positionId: string,
    assignmentData: AssignUserToPositionData
  ): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/org-chart/${companyId}/positions/${positionId}/assign`, {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  },

  // Remove user from position
  async removeUserFromPosition(
    companyId: string,
    positionId: string,
    assignmentId: string
  ): Promise<ApiResponse<any>> {
    return apiRequest<any>(
      `/org-chart/${companyId}/positions/${positionId}/assign/${assignmentId}`,
      {
        method: 'DELETE',
      }
    );
  },

  // Generate standard positions based on company size and industry
  async generateStandardPositions(
    companyId: string
  ): Promise<ApiResponse<{ generated: number; positions: OrganizationalPosition[] }>> {
    return apiRequest<{ generated: number; positions: OrganizationalPosition[] }>(
      `/org-chart/${companyId}/generate-standard-positions`,
      {
        method: 'POST',
      }
    );
  },
};

// Objectives API (individual objectives with user assignments)
export const objectivesApi = {
  // Get all objectives for a company
  async getObjectives(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        goal: string;
        measure: string;
        byWhen: string;
        targetDate: string | null;
        category: string;
        status: string;
        priority: string;
        description: string | null;
        assignedTo: string | null;
        divisionId: string | null;
        teamId: string | null;
        createdBy: string;
        createdAt: string;
        updatedAt: string;
        assigneeName: string | null;
        assigneeEmail: string | null;
        referenceNumber?: string;
        displayNumber?: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        goal: string;
        measure: string;
        byWhen: string;
        targetDate: string | null;
        category: string;
        status: string;
        priority: string;
        description: string | null;
        assignedTo: string | null;
        divisionId: string | null;
        teamId: string | null;
        createdBy: string;
        createdAt: string;
        updatedAt: string;
        assigneeName: string | null;
        assigneeEmail: string | null;
      }>
    >(`/objectives/${companyId}`);
  },

  // Create a new objective
  async createObjective(
    companyId: string,
    objective: {
      goal: string;
      measure: string;
      byWhen: string;
      targetDate?: string;
      category: string;
      assignedTo?: string;
      divisionId?: string;
      teamId?: string;
      priority?: string;
      description?: string;
    }
  ): Promise<
    ApiResponse<{
      id: string;
      goal: string;
      measure: string;
      byWhen: string;
      targetDate: string | null;
      category: string;
      status: string;
      priority: string;
      description: string | null;
      assignedTo: string | null;
      divisionId: string | null;
      teamId: string | null;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    return apiRequest<{
      id: string;
      goal: string;
      measure: string;
      byWhen: string;
      targetDate: string | null;
      category: string;
      status: string;
      priority: string;
      description: string | null;
      assignedTo: string | null;
      divisionId: string | null;
      teamId: string | null;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    }>(`/objectives/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(objective),
    });
  },

  // Update an objective
  async updateObjective(
    objectiveId: string,
    updates: {
      goal?: string;
      measure?: string;
      byWhen?: string;
      targetDate?: string;
      category?: string;
      status?: string;
      assignedTo?: string;
      divisionId?: string;
      teamId?: string;
      priority?: string;
      description?: string;
      referenceNumber?: string;
      displayNumber?: string;
    }
  ): Promise<
    ApiResponse<{
      id: string;
      goal: string;
      measure: string;
      byWhen: string;
      targetDate: string | null;
      category: string;
      status: string;
      priority: string;
      description: string | null;
      assignedTo: string | null;
      divisionId: string | null;
      teamId: string | null;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    return apiRequest<{
      id: string;
      goal: string;
      measure: string;
      byWhen: string;
      targetDate: string | null;
      category: string;
      status: string;
      priority: string;
      description: string | null;
      assignedTo: string | null;
      divisionId: string | null;
      teamId: string | null;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    }>(`/objectives/${objectiveId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Delete an objective
  async deleteObjective(objectiveId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/objectives/${objectiveId}`, {
      method: 'DELETE',
    });
  },

  // Get company members for assignment dropdown
  async getCompanyMembers(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        email: string;
        title: string | null;
        photo: string | null;
        role: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        name: string;
        email: string;
        title: string | null;
        photo: string | null;
        role: string;
      }>
    >(`/objectives/${companyId}/members`);
  },

  // Get company divisions for assignment dropdown
  async getCompanyDivisions(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        description: string | null;
        type: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        name: string;
        description: string | null;
        type: string;
      }>
    >(`/objectives/${companyId}/divisions`);
  },

  // Get company teams for assignment dropdown
  async getCompanyTeams(companyId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        description: string | null;
        type: string;
        divisionId: string | null;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        name: string;
        description: string | null;
        type: string;
        divisionId: string | null;
      }>
    >(`/objectives/${companyId}/teams`);
  },
};

// Measure Options API
export const measureOptionsApi = {
  // Get all measure options for a company
  async getMeasureOptions(
    companyId: string,
    objectiveTypeId?: string
  ): Promise<
    ApiResponse<
      Array<{
        id: string;
        label: string;
        description: string | null;
        category: string | null;
        objectiveTypeId: string;
        usageCount: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>
    >
  > {
    const queryParam = objectiveTypeId ? `?objectiveTypeId=${objectiveTypeId}` : '';
    return apiRequest<
      Array<{
        id: string;
        label: string;
        description: string | null;
        category: string | null;
        objectiveTypeId: string;
        usageCount: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>
    >(`/measure-options/${companyId}${queryParam}`);
  },

  // Create a new measure option
  async createMeasureOption(
    companyId: string,
    option: {
      label: string;
      description?: string;
      category?: string;
      objectiveTypeId?: string;
    }
  ): Promise<
    ApiResponse<{
      id: string;
      label: string;
      description: string | null;
      category: string | null;
      objectiveTypeId: string;
      usageCount: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    return apiRequest<{
      id: string;
      label: string;
      description: string | null;
      category: string | null;
      objectiveTypeId: string;
      usageCount: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/measure-options/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(option),
    });
  },

  // Increment usage count when a measure is used
  async incrementUsage(optionId: string): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/measure-options/${optionId}/increment-usage`, {
      method: 'POST',
    });
  },

  // Decrement usage count when a measure is no longer used
  async decrementUsage(optionId: string): Promise<ApiResponse<any>> {
    return apiRequest<any>(`/measure-options/${optionId}/decrement-usage`, {
      method: 'POST',
    });
  },
};

// Objective Types API
export const objectiveTypesApi = {
  // Get all objective types (default + company custom)
  async getObjectiveTypes(companyId: string): Promise<ApiResponse<ObjectiveType[]>> {
    return apiRequest<ObjectiveType[]>(`/objective-types?companyId=${companyId}`);
  },

  // Create a custom objective type
  async createObjectiveType(
    companyId: string,
    objectiveTypeData: CreateObjectiveTypeData
  ): Promise<ApiResponse<ObjectiveType>> {
    return apiRequest<ObjectiveType>('/objective-types', {
      method: 'POST',
      body: JSON.stringify({ ...objectiveTypeData, companyId }),
    });
  },

  // Update a custom objective type
  async updateObjectiveType(
    objectiveTypeId: string,
    objectiveTypeData: Partial<CreateObjectiveTypeData>
  ): Promise<ApiResponse<ObjectiveType>> {
    return apiRequest<ObjectiveType>(`/objective-types/${objectiveTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(objectiveTypeData),
    });
  },

  // Delete a custom objective type
  async deleteObjectiveType(objectiveTypeId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/objective-types/${objectiveTypeId}`, {
      method: 'DELETE',
    });
  },

  // Get measures for a specific objective type
  async getMeasuresForType(objectiveTypeId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        label: string;
        description: string | null;
        usageCount: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        label: string;
        description: string | null;
        usageCount: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>
    >(`/objective-types/${objectiveTypeId}/measures`);
  },
};

// Organizational Structure API functions
export const organizationalApi = {
  // Divisions
  async getDivisions(
    companyId: string,
    options?: { includeTeams?: boolean; includeStrategies?: boolean }
  ): Promise<ApiResponse<{ divisions: Division[]; totalCount: number }>> {
    const params = new URLSearchParams();
    if (options?.includeTeams) params.append('includeTeams', 'true');
    if (options?.includeStrategies) params.append('includeStrategies', 'true');

    const queryString = params.toString();
    return apiRequest<{ divisions: Division[]; totalCount: number }>(
      `/divisions/${companyId}${queryString ? `?${queryString}` : ''}`
    );
  },

  async createDivision(
    companyId: string,
    divisionData: CreateDivisionData
  ): Promise<ApiResponse<Division>> {
    return apiRequest<Division>(`/divisions/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(divisionData),
    });
  },

  async updateDivision(
    companyId: string,
    divisionId: string,
    divisionData: Partial<CreateDivisionData>
  ): Promise<ApiResponse<Division>> {
    return apiRequest<Division>(`/divisions/${companyId}/${divisionId}`, {
      method: 'PUT',
      body: JSON.stringify(divisionData),
    });
  },

  async getDivisionCascadeInfo(
    companyId: string,
    divisionId: string
  ): Promise<
    ApiResponse<{
      childDivisions: Array<{ id: string; name: string }>;
      teams: Array<{ id: string; name: string }>;
      strategies: Array<{ id: string; name: string }>;
      totalAffected: number;
    }>
  > {
    return apiRequest(`/divisions/${companyId}/${divisionId}/cascade-info`);
  },

  async deleteDivision(
    companyId: string,
    divisionId: string,
    cascade: boolean = false
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
      `/divisions/${companyId}/${divisionId}?cascade=${cascade}`,
      {
        method: 'DELETE',
      }
    );
  },

  // Teams
  async getTeams(
    companyId: string,
    divisionId?: string
  ): Promise<ApiResponse<{ teams: Team[]; totalCount: number }>> {
    const params = new URLSearchParams();
    if (divisionId) params.append('divisionId', divisionId);

    const queryString = params.toString();
    return apiRequest<{ teams: Team[]; totalCount: number }>(
      `/teams/${companyId}${queryString ? `?${queryString}` : ''}`
    );
  },

  async createTeam(companyId: string, teamData: CreateTeamData): Promise<ApiResponse<Team>> {
    return apiRequest<Team>(`/teams/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  },

  async updateTeam(
    companyId: string,
    teamId: string,
    teamData: Partial<CreateTeamData>
  ): Promise<ApiResponse<Team>> {
    return apiRequest<Team>(`/teams/${companyId}/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
  },

  async getTeamCascadeInfo(
    companyId: string,
    teamId: string
  ): Promise<
    ApiResponse<{
      childTeams: Array<{ id: string; name: string }>;
      members: Array<{ id: string; userName: string }>;
      strategies: Array<{ id: string; name: string }>;
      totalAffected: number;
    }>
  > {
    return apiRequest(`/teams/${companyId}/${teamId}/cascade-info`);
  },

  async deleteTeam(
    companyId: string,
    teamId: string,
    cascade: boolean = false
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/teams/${companyId}/${teamId}?cascade=${cascade}`, {
      method: 'DELETE',
    });
  },

  // Team Members
  async getTeamMembers(teamId: string): Promise<ApiResponse<OrganizationalTeamMember[]>> {
    return apiRequest<OrganizationalTeamMember[]>(`/teams/${teamId}/members`);
  },

  async addTeamMember(
    teamId: string,
    memberData: { userId: string; role?: string }
  ): Promise<ApiResponse<OrganizationalTeamMember>> {
    return apiRequest<OrganizationalTeamMember>(`/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
  },

  async updateTeamMemberRole(
    teamId: string,
    memberId: string,
    role: string
  ): Promise<ApiResponse<OrganizationalTeamMember>> {
    return apiRequest<OrganizationalTeamMember>(`/teams/${teamId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },

  async removeTeamMember(
    teamId: string,
    memberId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/teams/${teamId}/members/${memberId}`, {
      method: 'DELETE',
    });
  },

  // Hierarchical Strategies
  async getStrategies(
    companyId: string,
    options?: {
      ownerType?: 'company' | 'division' | 'team';
      ownerId?: string;
      includeChildren?: boolean;
    }
  ): Promise<ApiResponse<Strategy[]>> {
    const params = new URLSearchParams();
    if (options?.ownerType) params.append('ownerType', options.ownerType);
    if (options?.ownerId) params.append('ownerId', options.ownerId);
    if (options?.includeChildren) params.append('includeChildren', 'true');

    const queryString = params.toString();
    return apiRequest<Strategy[]>(
      `/strategies/${companyId}${queryString ? `?${queryString}` : ''}`
    );
  },

  async createStrategy(
    companyId: string,
    strategyData: CreateStrategyData
  ): Promise<ApiResponse<Strategy>> {
    return apiRequest<Strategy>(`/strategies/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(strategyData),
    });
  },

  async createCascadingStrategy(
    companyId: string,
    cascadeData: {
      parentStrategyId: string;
      ownerType: 'company' | 'division' | 'team';
      ownerId: string;
      name: string;
      summary?: string;
      inheritFrom?: {
        vision?: boolean;
        mission?: boolean;
        values?: boolean;
      };
    }
  ): Promise<ApiResponse<Strategy>> {
    return apiRequest<Strategy>(`/strategies/${companyId}/cascade`, {
      method: 'POST',
      body: JSON.stringify(cascadeData),
    });
  },

  async updateStrategy(
    companyId: string,
    strategyId: string,
    strategyData: Partial<CreateStrategyData>
  ): Promise<ApiResponse<Strategy>> {
    return apiRequest<Strategy>(`/strategies/${companyId}/${strategyId}`, {
      method: 'PUT',
      body: JSON.stringify(strategyData),
    });
  },

  async deleteStrategy(
    companyId: string,
    strategyId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/strategies/${companyId}/${strategyId}`, {
      method: 'DELETE',
    });
  },

  // Organizational Hierarchy
  async getOrganizationalHierarchy(companyId: string): Promise<ApiResponse<OrganizationalNode>> {
    return apiRequest<OrganizationalNode>(`/organization/${companyId}/hierarchy`);
  },

  async getOrganizationalMetrics(companyId: string): Promise<
    ApiResponse<{
      summary: {
        totalDivisions: number;
        totalTeams: number;
        totalStrategies: number;
        totalObjectives: number;
        totalTasks: number;
      };
      byLevel: {
        company: { strategies: number; objectives: number; tasks: number };
        divisions: Array<{
          id: string;
          name: string;
          strategies: number;
          objectives: number;
          tasks: number;
        }>;
        teams: Array<{
          id: string;
          name: string;
          strategies: number;
          objectives: number;
          tasks: number;
        }>;
      };
      taskStatusDistribution: Record<string, number>;
      objectiveStatusDistribution: Record<string, number>;
    }>
  > {
    return apiRequest<{
      summary: {
        totalDivisions: number;
        totalTeams: number;
        totalStrategies: number;
        totalObjectives: number;
        totalTasks: number;
      };
      byLevel: {
        company: { strategies: number; objectives: number; tasks: number };
        divisions: Array<{
          id: string;
          name: string;
          strategies: number;
          objectives: number;
          tasks: number;
        }>;
        teams: Array<{
          id: string;
          name: string;
          strategies: number;
          objectives: number;
          tasks: number;
        }>;
      };
      taskStatusDistribution: Record<string, number>;
      objectiveStatusDistribution: Record<string, number>;
    }>(`/organization/${companyId}/metrics`);
  },

  // Task Traceability
  async getTaskTraceability(taskId: string): Promise<
    ApiResponse<{
      task: any;
      objective?: any;
      strategy?: any;
      owner: {
        type: 'company' | 'division' | 'team';
        id: string;
        name: string;
      };
      hierarchy: Array<{
        type: 'company' | 'division' | 'team';
        id: string;
        name: string;
      }>;
      assignee?: any;
      team?: any;
      division?: any;
    }>
  > {
    return apiRequest<{
      task: any;
      objective?: any;
      strategy?: any;
      owner: {
        type: 'company' | 'division' | 'team';
        id: string;
        name: string;
      };
      hierarchy: Array<{
        type: 'company' | 'division' | 'team';
        id: string;
        name: string;
      }>;
      assignee?: any;
      team?: any;
      division?: any;
    }>(`/tasks/${taskId}/traceability`);
  },

  // Strategy Alignment
  async getStrategyAlignment(strategyId: string): Promise<
    ApiResponse<{
      strategy: any;
      parent?: any;
      children: any[];
      alignmentScore?: number;
      cascadingRules?: {
        inheritVision: boolean;
        inheritMission: boolean;
        inheritValues: boolean;
        customFields: string[];
      };
    }>
  > {
    return apiRequest<{
      strategy: any;
      parent?: any;
      children: any[];
      alignmentScore?: number;
      cascadingRules?: {
        inheritVision: boolean;
        inheritMission: boolean;
        inheritValues: boolean;
        customFields: string[];
      };
    }>(`/strategies/${strategyId}/alignment`);
  },

  // Generate organizational structure using AI
  async generateOrganizationalStructure(
    companyId: string,
    options: {
      primaryFocus: string;
      companyStage: string;
      functionalAreas: string[];
      geographicalScope: string;
      projectBasedWork: boolean;
      regulatoryCompliance: boolean;
      customerSegments: string[];
      additionalContext?: string;
    }
  ): Promise<
    ApiResponse<{
      structure: {
        divisions: Array<{
          name: string;
          type: 'business_unit' | 'functional' | 'geographical' | 'product' | 'project' | 'custom';
          description: string;
          teams: string[];
        }>;
        teams: Array<{
          name: string;
          type: 'operational' | 'project' | 'cross_functional' | 'leadership' | 'custom';
          description: string;
          division?: string;
        }>;
        positions: Array<{
          title: string;
          description: string;
          department?: string;
          level: number;
          parentTitle?: string;
        }>;
      };
      reasoning: string;
      recommendations: string[];
      usage: any;
      model: string;
    }>
  > {
    const response = await apiRequest<{
      structure: {
        divisions: Array<{
          name: string;
          type: 'business_unit' | 'functional' | 'geographical' | 'product' | 'project' | 'custom';
          description: string;
          teams: string[];
        }>;
        teams: Array<{
          name: string;
          type: 'operational' | 'project' | 'cross_functional' | 'leadership' | 'custom';
          description: string;
          division?: string;
        }>;
        positions: Array<{
          title: string;
          description: string;
          department?: string;
          level: number;
          parentTitle?: string;
        }>;
      };
      reasoning: string;
      recommendations: string[];
      usage: any;
      model: string;
    }>(`/organization/${companyId}/generate-structure`, {
      method: 'POST',
      body: JSON.stringify(options),
    });

    return response;
  },
};

// Questions API
export const questionsApi = {
  // Get questions by type
  async getByType(questionType: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        content: string;
        isPreset: boolean;
        questionTypeId: string;
      }>
    >
  > {
    return apiRequest<
      Array<{
        id: string;
        content: string;
        isPreset: boolean;
        questionTypeId: string;
      }>
    >(`/questions/type/${questionType}`);
  },

  // Get questions for a specific entity with responses
  async getEntityQuestions(
    entityType: string,
    companyId: string,
    entityKey?: string
  ): Promise<
    ApiResponse<
      Array<{
        id: string;
        content: string;
        isPreset: boolean;
        questionTypeId: string;
        responses: Array<{
          id: string;
          response: any;
          score: number;
          answeredAt: string;
        }>;
      }>
    >
  > {
    const params = entityKey ? `?entityKey=${entityKey}` : '';
    return apiRequest<
      Array<{
        id: string;
        content: string;
        isPreset: boolean;
        questionTypeId: string;
        responses: Array<{
          id: string;
          response: any;
          score: number;
          answeredAt: string;
        }>;
      }>
    >(`/questions/entity/${entityType}/${companyId}${params}`).then((result) => ({
      ...result,
      data: result.data?.map(currentAssessmentQuestion),
    }));
  },

  // Save question response
  async saveResponse(
    companyId: string,
    responseData: {
      questionId: string;
      entityType: string;
      entityKey?: string;
      response: {
        textValue?: string;
        selectedOptions?: string[];
        numericValue?: number;
        booleanValue?: boolean;
      };
    }
  ): Promise<
    ApiResponse<{
      id: string;
      response: any;
      score: number;
      answeredAt: string;
    }>
  > {
    return apiRequest<{
      id: string;
      response: any;
      score: number;
      answeredAt: string;
    }>(`/questions/responses/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(responseData),
    });
  },

  // Create custom question
  async createCustomQuestion(
    companyId: string,
    questionData: {
      questionType: string;
      content: string;
    }
  ): Promise<
    ApiResponse<{
      id: string;
      content: string;
      isPreset: boolean;
      questionTypeId: string;
    }>
  > {
    return apiRequest<{
      id: string;
      content: string;
      isPreset: boolean;
      questionTypeId: string;
    }>(`/questions/custom/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(questionData),
    });
  },
};

// Strategy Foundation Data API
export const strategyFoundationApi = {
  // Get strategy foundation data for a specific entity type
  async getFoundationData(
    companyId: string,
    entityType: string
  ): Promise<
    ApiResponse<{
      [key: string]: string[];
    }>
  > {
    return apiRequest<{
      [key: string]: string[];
    }>(`/strategies/${companyId}/foundation-data/${entityType}`);
  },

  // Save strategy foundation data for a specific entity type
  async saveFoundationData(
    companyId: string,
    entityType: string,
    data: {
      [key: string]: string[];
    }
  ): Promise<
    ApiResponse<{
      message: string;
    }>
  > {
    return apiRequest<{
      message: string;
    }>(`/strategies/${companyId}/foundation-data/${entityType}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// New strategy-related interfaces
export interface StrategyTemplate {
  id: string;
  title: string;
  description: string;
  objectiveTypeId: string;
  category: string;
  isDefault: boolean;
  companyId?: string;
  tags?: string[];
  metadata?: {
    complexity?: 'low' | 'medium' | 'high';
    timeframe?: 'short' | 'medium' | 'long';
    resources?: string[];
    prerequisites?: string[];
  };
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  // Joined data
  objectiveTypeName?: string;
  objectiveTypeColor?: string;
  objectiveTypeIcon?: string;
}

export interface CreateStrategyTemplateData {
  title: string;
  description: string;
  objectiveTypeId: string;
  category: string;
  tags?: string[];
  metadata?: {
    complexity?: 'low' | 'medium' | 'high';
    timeframe?: 'short' | 'medium' | 'long';
    resources?: string[];
    prerequisites?: string[];
  };
}

export interface SelectedStrategy {
  id: string;
  companyId: string;
  objectiveId: string;
  strategyTemplateId?: string;
  title: string;
  description: string;
  isCustom: boolean;
  status: 'planned' | 'in_progress' | 'completed' | 'on_hold';
  priority: 'high' | 'medium' | 'low';
  startDate?: string;
  targetDate?: string;
  assignedTo?: string;
  notes?: string;
  progress: number;
  progressMode?: 'calculated' | 'manual';
  // NEW: Reference numbering
  referenceNumber?: string;
  displayNumber?: string;
  aiGenerated?: boolean; // Track AI-generated strategies
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // Joined data
  objectiveGoal?: string;
  templateCategory?: string;
  assigneeName?: string;
  assigneeEmail?: string;
}

export interface CreateSelectedStrategyData {
  objectiveId: string;
  strategyTemplateId?: string;
  title: string;
  description: string;
  isCustom?: boolean;
  priority?: 'high' | 'medium' | 'low';
  startDate?: string;
  targetDate?: string;
  assignedTo?: string;
  notes?: string;
  aiGenerated?: boolean; // Track AI-generated strategies
}

export interface UpdateSelectedStrategyData {
  title?: string;
  description?: string;
  status?: 'planned' | 'in_progress' | 'completed' | 'on_hold';
  priority?: 'high' | 'medium' | 'low';
  startDate?: string;
  targetDate?: string;
  assignedTo?: string;
  notes?: string;
  progress?: number;
  progressMode?: 'calculated' | 'manual';
  referenceNumber?: string;
  displayNumber?: string;
}

// Strategy Templates API
export const strategyTemplatesApi = {
  // Get strategy templates by objective type
  async getStrategyTemplates(
    companyId: string,
    objectiveTypeId?: string
  ): Promise<ApiResponse<StrategyTemplate[]>> {
    const params = new URLSearchParams({ companyId });
    if (objectiveTypeId) {
      params.append('objectiveTypeId', objectiveTypeId);
    }
    return apiRequest<StrategyTemplate[]>(`/strategy-templates?${params.toString()}`);
  },

  // Create a custom strategy template
  async createStrategyTemplate(
    companyId: string,
    templateData: CreateStrategyTemplateData
  ): Promise<ApiResponse<StrategyTemplate>> {
    return apiRequest<StrategyTemplate>('/strategy-templates', {
      method: 'POST',
      body: JSON.stringify({ ...templateData, companyId }),
    });
  },

  // Update a custom strategy template
  async updateStrategyTemplate(
    templateId: string,
    companyId: string,
    templateData: Partial<CreateStrategyTemplateData>
  ): Promise<ApiResponse<StrategyTemplate>> {
    return apiRequest<StrategyTemplate>(`/strategy-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...templateData, companyId }),
    });
  },

  // Delete a custom strategy template
  async deleteStrategyTemplate(
    templateId: string,
    companyId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/strategy-templates/${templateId}`, {
      method: 'DELETE',
      body: JSON.stringify({ companyId }),
    });
  },
};

// Selected Strategies API
export const selectedStrategiesApi = {
  // Get selected strategies for a company
  async getSelectedStrategies(
    companyId: string,
    objectiveId?: string
  ): Promise<ApiResponse<SelectedStrategy[]>> {
    const params = objectiveId ? `?objectiveId=${objectiveId}` : '';
    return apiRequest<SelectedStrategy[]>(`/selected-strategies/${companyId}${params}`);
  },

  // Create a selected strategy
  async createSelectedStrategy(
    companyId: string,
    strategyData: CreateSelectedStrategyData
  ): Promise<ApiResponse<SelectedStrategy>> {
    return apiRequest<SelectedStrategy>(`/selected-strategies/${companyId}`, {
      method: 'POST',
      body: JSON.stringify(strategyData),
    });
  },

  // Update a selected strategy
  async updateSelectedStrategy(
    strategyId: string,
    strategyData: UpdateSelectedStrategyData
  ): Promise<ApiResponse<SelectedStrategy>> {
    return apiRequest<SelectedStrategy>(`/selected-strategies/${strategyId}`, {
      method: 'PUT',
      body: JSON.stringify(strategyData),
    });
  },

  // Delete a selected strategy
  async deleteSelectedStrategy(strategyId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/selected-strategies/${strategyId}`, {
      method: 'DELETE',
    });
  },
};

// Billing and subscription types
export interface BillingInfo {
  plan: 'free' | 'founder' | 'pro';
  billingCycle: 'monthly' | 'yearly';
  billingStatus: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';
  currentUserCount: number;
  proUserCount: number;
  isInTrial: boolean;
  trialDaysRemaining: number;
  hasTrialAvailable: boolean;
  isFounder: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  nextPaymentDate?: string;
  cancellingOn?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  billingEmail?: string;
  subscription?: any; // Stripe subscription object
  actualAmount?: number; // Actual amount from Stripe (includes discounts)
  actualCurrency?: string;
  discountInfo?: {
    couponId: string;
    name?: string;
    amountOff?: number;
    percentOff?: number;
    duration: string;
    durationInMonths?: number;
  };
}

export interface CreateCheckoutSessionData {
  billingCycle: 'monthly' | 'yearly';
  userCount: number;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface TrialResponse {
  sessionId?: string;
  url?: string;
  message: string;
  trialEndDate?: string;
}

export interface BillingPortalResponse {
  url: string;
}

export interface UserBillingInfo {
  isFounder: boolean;
  status: string | null; // 'active' | 'canceled' | 'past_due' | null
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  actualAmount: number | null; // in cents
  discountInfo?: {
    couponId: string;
    name?: string;
    amountOff?: number;
    percentOff?: number;
    duration: string;
    durationInMonths?: number;
  } | null;
}

// Billing API
export const billingApi = {
  // Get billing information for a company
  async getBillingInfo(companyId: string): Promise<ApiResponse<BillingInfo>> {
    return apiRequest<BillingInfo>(`/billing/${companyId}`);
  },

  // User-level Founder checkout — no company needed upfront
  async createFounderCheckoutSessionForUser(): Promise<ApiResponse<CheckoutSessionResponse>> {
    return apiRequest<CheckoutSessionResponse>('/billing/founder/checkout', {
      method: 'POST',
    });
  },

  // Create Stripe checkout session for Founder subscription (legacy — existing company)
  async createFounderCheckoutSession(
    companyId: string
  ): Promise<ApiResponse<CheckoutSessionResponse>> {
    return apiRequest<CheckoutSessionResponse>(`/billing/${companyId}/checkout-founder`, {
      method: 'POST',
    });
  },

  // Create Stripe checkout session for Pro Activation subscription
  async createCheckoutSession(
    companyId: string,
    sessionData: CreateCheckoutSessionData
  ): Promise<ApiResponse<CheckoutSessionResponse>> {
    return apiRequest<CheckoutSessionResponse>(`/billing/${companyId}/checkout`, {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  },

  // Cancel subscription
  async cancelSubscription(companyId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`/billing/${companyId}/cancel`, {
      method: 'POST',
    });
  },

  // Create billing portal session
  async createBillingPortalSession(companyId: string): Promise<ApiResponse<BillingPortalResponse>> {
    return apiRequest<BillingPortalResponse>(`/billing/${companyId}/portal`, {
      method: 'POST',
    });
  },

  // Get user-level Founder subscription billing info
  async getUserBillingInfo(): Promise<ApiResponse<UserBillingInfo>> {
    return apiRequest<UserBillingInfo>('/billing/user/me');
  },

  // Create Stripe Billing Portal session for user's Founder subscription
  async createUserBillingPortalSession(): Promise<ApiResponse<BillingPortalResponse>> {
    return apiRequest<BillingPortalResponse>('/billing/user/portal', {
      method: 'POST',
    });
  },

  // Open Stripe portal directly on the payment method update screen
  async openPaymentMethodsPortal(): Promise<ApiResponse<BillingPortalResponse>> {
    return apiRequest<BillingPortalResponse>('/billing/user/payment-methods', {
      method: 'POST',
    });
  },

  // Cancel user's Founder subscription
  async cancelFounderSubscription(): Promise<
    ApiResponse<{ message: string; cancellingOn?: string }>
  > {
    return apiRequest<{ message: string; cancellingOn?: string }>('/billing/user/cancel', {
      method: 'POST',
    });
  },
};

// Assessment Progress API - Efficient single calls to calculate all progress
export interface AssessmentProgressData {
  sections: {
    pestel: number;
    fiveForces: number;
    competitors: number;
    drivingForces: number;
  };
  overall: number;
  details: {
    pestel: {
      completed: number;
      total: number;
    };
    fiveForces: {
      completed: number;
      total: number;
    };
    competitors: {
      count: number;
      target: number;
    };
    drivingForces: {
      analyzed: number;
      questionsAnswered: number;
      totalAnswered: number;
      target: number;
    };
  };
}

export interface InternalAssessmentProgressData {
  sections: {
    swot: number;
    industrySuccessFactors: number;
  };
  overall: number;
  details: {
    swot: {
      completed: number;
      total: number;
    };
    industrySuccessFactors: {
      hasSelections: boolean;
    };
  };
}

export interface FormulationProgressData {
  sections: {
    summary: number;
    strategies: number;
  };
  overall: number;
  prerequisites: {
    foundation: boolean;
    external: boolean;
    internal: boolean;
  };
  details: {
    summary: {
      prerequisitesComplete: boolean;
      foundationComplete: boolean;
      externalComplete: boolean;
      internalComplete: boolean;
    };
    strategies: {
      count: number;
      target: number;
    };
  };
}

export const assessmentProgressApi = {
  async getExternalProgress(companyId: string): Promise<ApiResponse<AssessmentProgressData>> {
    return apiRequest<AssessmentProgressData>(`/assessment-progress/${companyId}/external`);
  },

  async getInternalProgress(
    companyId: string
  ): Promise<ApiResponse<InternalAssessmentProgressData>> {
    return apiRequest<InternalAssessmentProgressData>(`/assessment-progress/${companyId}/internal`);
  },

  async getFormulationProgress(companyId: string): Promise<ApiResponse<FormulationProgressData>> {
    return apiRequest<FormulationProgressData>(`/assessment-progress/${companyId}/formulation`);
  },
};

// Auth API
export const authApi = {
  async getCurrentUser(): Promise<ApiResponse<CurrentUser>> {
    return apiRequest<CurrentUser>('/auth/me');
  },

  async syncMemberships(): Promise<
    ApiResponse<{
      syncedCount: number;
      alreadyExistsCount: number;
      companies: Array<{
        companyId: string;
        companyName: string;
        action: 'synced' | 'already_exists';
        role: string;
      }>;
    }>
  > {
    return apiRequest('/auth/sync-memberships', {
      method: 'POST',
    });
  },
};

// Admin API
export const adminApi = {
  async getUsers(): Promise<ApiResponse<AdminUser[]>> {
    return apiRequest<AdminUser[]>('/admin/users');
  },

  async updateUserSuperStatus(
    userId: string,
    superUser: boolean
  ): Promise<ApiResponse<{ id: string; name: string; email: string; superUser: boolean }>> {
    return apiRequest<{ id: string; name: string; email: string; superUser: boolean }>(
      `/admin/users/${userId}/super-user`,
      {
        method: 'PATCH',
        body: JSON.stringify({ superUser }),
      }
    );
  },

  async getCompanies(): Promise<ApiResponse<AdminCompany[]>> {
    return apiRequest<AdminCompany[]>('/admin/companies');
  },

  async getCompanyDetails(companyId: string): Promise<
    ApiResponse<{
      company: any;
      members: any[];
      pendingInvitations: any[];
    }>
  > {
    return apiRequest<{
      company: any;
      members: any[];
      pendingInvitations: any[];
    }>(`/admin/companies/${companyId}`);
  },

  async toggleCompanyProFeatures(
    companyId: string,
    enablePro: boolean,
    reason?: string
  ): Promise<
    ApiResponse<{
      companyId: string;
      isPro: boolean;
      subscriptionTier: string;
      message: string;
    }>
  > {
    return apiRequest<{
      companyId: string;
      isPro: boolean;
      subscriptionTier: string;
      message: string;
    }>(`/admin/companies/${companyId}/pro-features`, {
      method: 'PATCH',
      body: JSON.stringify({ enablePro, reason }),
    });
  },

  async getStats(): Promise<ApiResponse<AdminStats>> {
    return apiRequest<AdminStats>('/admin/stats');
  },

  // Meta Lead Ads integration (super user)
  async getMetaLeadsSettings(): Promise<
    ApiResponse<{
      verifyToken: string;
      verifyTokenSet: boolean;
      pages: Array<{
        id: string;
        metaPageId: string;
        pageName: string | null;
        isSubscribed: boolean;
        createdAt: string;
      }>;
      connectAvailable?: boolean;
    }>
  > {
    return apiRequest('/admin/meta-leads/settings');
  },

  async getMetaLeadsConnectUrl(): Promise<ApiResponse<{ url: string | null }>> {
    return apiRequest('/admin/meta-leads/connect/url');
  },

  async getMetaLeadsConnectPages(
    key: string
  ): Promise<ApiResponse<{ pages: Array<{ id: string; name: string }> }>> {
    return apiRequest(`/admin/meta-leads/connect/pages?key=${encodeURIComponent(key)}`);
  },

  async selectMetaLeadsConnectPage(
    key: string,
    pageId: string
  ): Promise<
    ApiResponse<{
      id: string;
      metaPageId: string;
      pageName: string | null;
      isSubscribed: boolean;
      createdAt: string;
    }>
  > {
    return apiRequest('/admin/meta-leads/connect/select', {
      method: 'POST',
      body: JSON.stringify({ key, pageId }),
    });
  },

  async putMetaLeadsSettings(
    verifyToken: string | null
  ): Promise<ApiResponse<{ verifyToken: string }>> {
    return apiRequest('/admin/meta-leads/settings', {
      method: 'PUT',
      body: JSON.stringify({ verifyToken }),
    });
  },

  async getMetaLeadsPages(): Promise<
    ApiResponse<
      Array<{
        id: string;
        metaPageId: string;
        pageName: string | null;
        isSubscribed: boolean;
        createdAt: string;
      }>
    >
  > {
    return apiRequest('/admin/meta-leads/pages');
  },

  async addMetaLeadsPage(
    metaPageId: string,
    accessToken: string,
    pageName?: string
  ): Promise<
    ApiResponse<{
      id: string;
      metaPageId: string;
      pageName: string | null;
      isSubscribed: boolean;
      createdAt: string;
    }>
  > {
    return apiRequest('/admin/meta-leads/pages', {
      method: 'POST',
      body: JSON.stringify({ metaPageId, accessToken, pageName }),
    });
  },

  async updateMetaLeadsPage(
    id: string,
    updates: { pageName?: string; accessToken?: string }
  ): Promise<
    ApiResponse<{
      id: string;
      metaPageId: string;
      pageName: string | null;
      isSubscribed: boolean;
      createdAt: string;
    }>
  > {
    return apiRequest(`/admin/meta-leads/pages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async deleteMetaLeadsPage(id: string): Promise<ApiResponse<void>> {
    return apiRequest(`/admin/meta-leads/pages/${id}`, { method: 'DELETE' });
  },

  async subscribeMetaLeadsPage(id: string): Promise<ApiResponse<void>> {
    return apiRequest(`/admin/meta-leads/pages/${id}/subscribe`, { method: 'POST' });
  },

  async testMetaLeadsPage(id: string): Promise<ApiResponse<{ valid: boolean; name?: string }>> {
    return apiRequest(`/admin/meta-leads/pages/${id}/test`, { method: 'POST' });
  },
};

// Execution API (comprehensive execution hierarchy)
export const executionApi = {
  // Get comprehensive execution hierarchy (objectives -> strategies -> projects -> tasks)
  async getExecutionHierarchy(companyId: string): Promise<
    ApiResponse<{
      objectives: Array<{
        id: string;
        goal: string;
        measure: string;
        targetDate: string | null;
        category: string;
        status: string;
        priority: string;
        description: string | null;
        assignedTo: string | null;
        assigneeName: string | null;
        referenceNumber?: string;
        displayNumber?: string;
        strategies: Array<{
          id: string;
          title: string;
          description: string;
          status: string;
          priority: string;
          progress: number;
          startDate: string | null;
          targetDate: string | null;
          assignedTo: string | null;
          assigneeName: string | null;
          referenceNumber?: string;
          displayNumber?: string;
          projects: Array<{
            id: string;
            title: string;
            description: string;
            status: string;
            priority: string;
            progress: number;
            startDate: string | null;
            targetDate: string | null;
            actualStartDate: string | null;
            actualEndDate: string | null;
            projectManager: string | null;
            projectManagerName: string | null;
            assignedTo: string | null;
            assigneeName: string | null;
            estimatedHours: number | null;
            actualHours: number | null;
            budgetAllocated: number | null;
            budgetUsed: number | null;
            referenceNumber?: string;
            displayNumber?: string;
            taskCount: number;
            completedTasks: number;
            deliverables: Array<{
              name: string;
              description?: string;
              dueDate?: string;
              status: string;
              assignedTo?: string;
            }>;
          }>;
        }>;
      }>;
      progressSummary: {
        totalObjectives: number;
        objectivesInProgress: number;
        objectivesCompleted: number;
        totalStrategies: number;
        strategiesInProgress: number;
        strategiesCompleted: number;
        totalProjects: number;
        projectsInProgress: number;
        projectsCompleted: number;
        totalTasks: number;
        tasksInProgress: number;
        tasksCompleted: number;
        overallProgress: number;
      };
    }>
  > {
    return apiRequest<{
      objectives: Array<{
        id: string;
        goal: string;
        measure: string;
        targetDate: string | null;
        category: string;
        status: string;
        priority: string;
        description: string | null;
        assignedTo: string | null;
        assigneeName: string | null;
        referenceNumber?: string;
        displayNumber?: string;
        strategies: Array<{
          id: string;
          title: string;
          description: string;
          status: string;
          priority: string;
          progress: number;
          startDate: string | null;
          targetDate: string | null;
          assignedTo: string | null;
          assigneeName: string | null;
          referenceNumber?: string;
          displayNumber?: string;
          projects: Array<{
            id: string;
            title: string;
            description: string;
            status: string;
            priority: string;
            progress: number;
            startDate: string | null;
            targetDate: string | null;
            actualStartDate: string | null;
            actualEndDate: string | null;
            projectManager: string | null;
            projectManagerName: string | null;
            assignedTo: string | null;
            assigneeName: string | null;
            estimatedHours: number | null;
            actualHours: number | null;
            budgetAllocated: number | null;
            budgetUsed: number | null;
            referenceNumber?: string;
            displayNumber?: string;
            taskCount: number;
            completedTasks: number;
            deliverables: Array<{
              name: string;
              description?: string;
              dueDate?: string;
              status: string;
              assignedTo?: string;
            }>;
          }>;
        }>;
      }>;
      progressSummary: {
        totalObjectives: number;
        objectivesInProgress: number;
        objectivesCompleted: number;
        totalStrategies: number;
        strategiesInProgress: number;
        strategiesCompleted: number;
        totalProjects: number;
        projectsInProgress: number;
        projectsCompleted: number;
        totalTasks: number;
        tasksInProgress: number;
        tasksCompleted: number;
        overallProgress: number;
      };
    }>(`/companies/${companyId}/execution`);
  },
};

// Blog Types
export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  postCount?: number;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  coverImage?: string;
  status: 'draft' | 'published';
  publishedAt?: string;
  seoTitle?: string;
  seoDescription?: string;
  openGraphImage?: string;
  aiGenerated: boolean;
  aiPrompt?: string;
  viewCount: number;
  readingTimeMinutes: number;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    name: string;
    email: string;
    photo?: string;
    title?: string;
  };
  category?: BlogCategory;
  tags: BlogTag[];
}

export interface BlogTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color: string;
  postCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBlogPostData {
  title: string;
  content: string;
  excerpt?: string;
  coverImage?: string;
  categoryId?: string;
  status?: 'draft' | 'published';
  seoTitle?: string;
  seoDescription?: string;
  openGraphImage?: string;
  tagIds?: string[];
  aiGenerated?: boolean;
  aiPrompt?: string;
}

export interface UpdateBlogPostData {
  title?: string;
  content?: string;
  excerpt?: string;
  coverImage?: string;
  categoryId?: string;
  status?: 'draft' | 'published';
  seoTitle?: string;
  seoDescription?: string;
  openGraphImage?: string;
  tagIds?: string[];
}

export interface CreateBlogTagData {
  name: string;
  description?: string;
  color?: string;
}

export interface BlogListResponse {
  posts: BlogPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BlogGenerationOptions {
  topic: string;
  targetAudience?: string;
  keywords?: string[];
  tone?: 'professional' | 'casual' | 'inspirational' | 'educational' | 'conversational';
  wordCount?: number;
  includeOutline?: boolean;
  style?: 'how-to' | 'listicle' | 'case-study' | 'opinion' | 'news' | 'tutorial';
}

export interface BlogGenerationResult {
  title: string;
  content: string;
  excerpt: string;
  seoTitle: string;
  seoDescription: string;
  suggestedCategory: string;
  suggestedTags: string[];
  readingTimeMinutes: number;
  outline?: {
    sections: Array<{
      heading: string;
      points: string[];
    }>;
  };
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  model?: string;
}

export interface ContentSuggestion {
  id: string;
  title: string;
  description: string;
  topic: string;
  targetAudience: string;
  keywords: string[];
  tone: 'professional' | 'casual' | 'inspirational' | 'educational' | 'conversational';
  style: 'how-to' | 'listicle' | 'case-study' | 'opinion' | 'news' | 'tutorial';
  reasoning: string;
  confidence: number; // 0-100
}

export interface ContentAnalysisOptions {
  analyzeCount?: number; // Number of recent posts to analyze (default: 10)
  suggestionsCount?: number; // Number of suggestions to generate (default: 5)
  focusAreas?: string[]; // Optional focus areas like "SEO", "engagement", "thought leadership"
}

export interface ContentAnalysisResult {
  suggestions: ContentSuggestion[];
  analysis: {
    commonTopics: string[];
    popularKeywords: string[];
    contentGaps: string[];
    audienceInsights: string;
    trendingStyles: string[];
  };
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    estimated_cost: number;
  };
}

// Unsplash API Types
export interface UnsplashPhoto {
  id: string;
  description: string | null;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  width: number;
  height: number;
  color: string;
  user: {
    id: string;
    name: string;
    username: string;
    profile_image?: string;
    portfolio_url?: string;
    links: {
      html: string;
    };
  };
  links: {
    html: string;
    download_location: string;
  };
  likes?: number;
  exif?: {
    make?: string;
    model?: string;
    focal_length?: string;
    aperture?: string;
    iso?: number;
    exposure_time?: string;
  };
}

export interface UnsplashSearchParams {
  query: string;
  page?: number;
  per_page?: number;
  orientation?: 'landscape' | 'portrait' | 'squarish';
}

export interface UnsplashSearchResult {
  photos: UnsplashPhoto[];
  total: number;
  total_pages: number;
  page: number;
  per_page: number;
}

// Blog API functions
export const blogApi = {
  // Admin functions (requires admin access)
  admin: {
    // Get all posts for admin
    async getPosts(params?: {
      status?: string;
      search?: string;
      tag?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<BlogListResponse>> {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.append('status', params.status);
      if (params?.search) searchParams.append('search', params.search);
      if (params?.tag) searchParams.append('tag', params.tag);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());

      const queryString = searchParams.toString();
      return apiRequest<BlogListResponse>(`/blog/posts${queryString ? `?${queryString}` : ''}`);
    },

    // Get single post by ID (admin)
    async getPost(id: string): Promise<ApiResponse<BlogPost>> {
      return apiRequest<BlogPost>(`/blog/posts/${id}`);
    },

    // Create new post
    async createPost(postData: CreateBlogPostData): Promise<ApiResponse<BlogPost>> {
      return apiRequest<BlogPost>('/blog/posts', {
        method: 'POST',
        body: JSON.stringify(postData),
      });
    },

    // Update post
    async updatePost(id: string, postData: UpdateBlogPostData): Promise<ApiResponse<BlogPost>> {
      return apiRequest<BlogPost>(`/blog/posts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(postData),
      });
    },

    // Delete post
    async deletePost(id: string): Promise<ApiResponse<{ message: string }>> {
      return apiRequest<{ message: string }>(`/blog/posts/${id}`, {
        method: 'DELETE',
      });
    },

    // Generate blog content with AI
    async generateContent(
      options: BlogGenerationOptions
    ): Promise<ApiResponse<BlogGenerationResult>> {
      return apiRequest<BlogGenerationResult>('/blog/generate', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },

    // Generate blog outline with AI
    async generateOutline(topic: string): Promise<
      ApiResponse<{
        outline: {
          sections: Array<{
            heading: string;
            points: string[];
          }>;
        };
        usage?: any;
        model?: string;
      }>
    > {
      return apiRequest('/blog/generate-outline', {
        method: 'POST',
        body: JSON.stringify({ topic }),
      });
    },

    // Analyze content and get AI suggestions
    async analyzeAndSuggest(
      options: ContentAnalysisOptions = {}
    ): Promise<ApiResponse<ContentAnalysisResult>> {
      return apiRequest<ContentAnalysisResult>('/blog/analyze-and-suggest', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },
  },

  // Public functions (no auth required)
  public: {
    // Get published posts for public
    async getPosts(params?: {
      search?: string;
      tag?: string;
      category?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<BlogListResponse>> {
      const searchParams = new URLSearchParams();
      if (params?.search) searchParams.append('search', params.search);
      if (params?.tag) searchParams.append('tag', params.tag);
      if (params?.category) searchParams.append('category', params.category);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());

      const queryString = searchParams.toString();
      return apiRequest<BlogListResponse>(
        `/blog/posts/public${queryString ? `?${queryString}` : ''}`
      );
    },

    // Get single published post by slug
    async getPostBySlug(slug: string): Promise<ApiResponse<BlogPost>> {
      return apiRequest<BlogPost>(`/blog/posts/${slug}/public`);
    },
  },

  // Category management
  categories: {
    // Get all categories
    async getCategories(): Promise<ApiResponse<BlogCategory[]>> {
      return apiRequest<BlogCategory[]>('/blog/categories');
    },
  },

  // Tag management
  tags: {
    // Get all tags
    async getTags(): Promise<ApiResponse<BlogTag[]>> {
      return apiRequest<BlogTag[]>('/blog/tags');
    },

    // Create new tag (admin only)
    async createTag(tagData: CreateBlogTagData): Promise<ApiResponse<BlogTag>> {
      return apiRequest<BlogTag>('/blog/tags', {
        method: 'POST',
        body: JSON.stringify(tagData),
      });
    },

    // Delete tag (admin only)
    async deleteTag(id: string): Promise<ApiResponse<{ message: string }>> {
      return apiRequest<{ message: string }>(`/blog/tags/${id}`, {
        method: 'DELETE',
      });
    },
  },
};

// Media API functions
export const mediaApi = {
  // Unsplash integration
  unsplash: {
    // Search Unsplash photos
    async search(params: UnsplashSearchParams): Promise<ApiResponse<UnsplashSearchResult>> {
      const searchParams = new URLSearchParams();
      searchParams.append('query', params.query);
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params.orientation) searchParams.append('orientation', params.orientation);

      return apiRequest<UnsplashSearchResult>(`/media/unsplash/search?${searchParams.toString()}`);
    },

    // Get single Unsplash photo details
    async getPhoto(id: string): Promise<ApiResponse<UnsplashPhoto>> {
      return apiRequest<UnsplashPhoto>(`/media/unsplash/photo/${id}`);
    },

    // Track download (required by Unsplash API)
    async trackDownload(downloadLocation: string): Promise<ApiResponse<any>> {
      return apiRequest('/media/unsplash/download', {
        method: 'POST',
        body: JSON.stringify({ download_location: downloadLocation }),
      });
    },
  },

  // File upload and management
  files: {
    // Upload a file
    async upload(file: File): Promise<ApiResponse<any>> {
      const formData = new FormData();
      formData.append('file', file);

      return apiRequest('/media/upload', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set content-type for FormData
      });
    },

    // Get all media files
    async getFiles(params?: {
      page?: number;
      limit?: number;
      type?: string;
    }): Promise<ApiResponse<any>> {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.type) searchParams.append('type', params.type);

      const queryString = searchParams.toString();
      return apiRequest(`/media${queryString ? `?${queryString}` : ''}`);
    },

    // Get single media file
    async getFile(id: string): Promise<ApiResponse<any>> {
      return apiRequest(`/media/${id}`);
    },

    // Update media file (alt text, etc.)
    async updateFile(id: string, data: { altText?: string }): Promise<ApiResponse<any>> {
      return apiRequest(`/media/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    // Delete media file
    async deleteFile(id: string): Promise<ApiResponse<{ message: string }>> {
      return apiRequest(`/media/${id}`, {
        method: 'DELETE',
      });
    },
  },
};

export default companiesApi;
